import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '@sw/api-client';
import { AUTH_ME_QUERY_KEY } from './AuthProvider';

export interface UseLogoutOptions {
  apiClient: ApiClient;
  /** Path del endpoint. Default: `/api/client/auth/logout`. */
  logoutPath?: string;
  /** Callback invocado después de logout exitoso (típicamente: redirigir a /login). */
  onSuccess?: () => void;
}

/**
 * Mutation para POST `/api/client/auth/logout`. Tras éxito:
 * - El backend ya borró las cookies (cp_session + cp_csrf) + envió Clear-Site-Data.
 * - Invalidamos el query `auth/me` para que el AuthProvider refetchee → 401 → user=null.
 * - Invocamos `onSuccess` (caller típicamente redirige a /login).
 *
 * Story SW-311.
 */
export function useLogout({
  apiClient,
  logoutPath = '/api/client/auth/logout',
  onSuccess,
}: UseLogoutOptions) {
  const queryClient = useQueryClient();

  return useMutation<void, Error>({
    mutationFn: async () => {
      await apiClient.post<void>(logoutPath);
    },
    onSuccess: async () => {
      // Invalidar el /me — el siguiente fetch traerá user=null (401) y el provider
      // actualiza el state.
      await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
      onSuccess?.();
    },
  });
}
